import { useState, useEffect } from "react";
import { API_BASE_URL, fetchAuthenticated } from "../utils/config";

function CompanyGrouping({ accounts, setAccounts }) {
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedToMove, setSelectedToMove] = useState(new Set());
  const [groups, setGroups] = useState([]);

  // Fetch all groups 
  useEffect(() => {
    const loadGroups = async () => {
      try {
        const res = await fetchAuthenticated(
          `${API_BASE_URL}/companyProfile/getAllCompanyGroupings`,
          { method: "GET" }
        );
        const json = await res.json();
        setGroups(json.data || []);
      } catch (err) {
        console.error("Error loading groups", err);
      }
    };

    loadGroups();
  }, []);

  // Accounts that are not in any group 
  const ungrouped = accounts.filter((a) => !a.companyGroup);

  // Toggle checkbox selection
  const toggleSelect = (id) => {
    setSelectedToMove((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // CREATE GROUP
  const createGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;

    // Prevent duplicates
    if (
      groups.some(
        (g) => g.groupingName.toLowerCase() === name.toLowerCase()
      )
    ) {
      alert("A group with that name already exists.");
      return;
    }

    try {
      const res = await fetchAuthenticated(
        `${API_BASE_URL}/companyProfile/createGrouping`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ groupingName: name }),
        }
      );

      const data = await res.json();
      if (!data.groupingId) {
        alert("Backend didn't return groupingId");
        return;
      }

      // Add to groups list
      setGroups((prev) => [
        ...prev,
        { id: data.groupingId, groupingName: name }
      ]);

      setNewGroupName("");
      alert("Group created!");

    } catch (err) {
      console.error("Error creating group", err);
      alert("Failed to create group");
    }
  };

  // ASSIGN ACCOUNTS TO GROUP
  const assignToGroup = async () => {
    if (!selectedGroupId) return alert("Choose a group first");
    if (selectedToMove.size === 0) return alert("Select accounts");

    const accountIds = Array.from(selectedToMove);

    try {
      await fetchAuthenticated(
        `${API_BASE_URL}/companyProfile/addAccountsToGrouping`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            groupingId: selectedGroupId,
            accountIds,
          }),
        }
      );

      const groupName =
        groups.find((g) => g.id === selectedGroupId)?.groupingName ?? null;

      // Update UI
      setAccounts((prev) =>
        prev.map((acc) =>
          accountIds.includes(acc.accountId)
            ? {
                ...acc,
                companyGroup: selectedGroupId,
                groupName,
              }
            : acc
        )
      );

      setSelectedToMove(new Set());
      alert("Assigned!");

    } catch (err) {
      console.error("Error assigning accounts", err);
      alert("Failed to assign");
    }
  };

  // REMOVE ACCOUNT FROM GROUP
  const removeFromGroup = async (accountId) => {
    const acc = accounts.find((a) => a.accountId === accountId);
    if (!acc?.companyGroup) return alert("Not in a group");

    try {
      await fetchAuthenticated(
        `${API_BASE_URL}/companyProfile/removeAccountsFromGrouping`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            groupingId: acc.companyGroup,
            accountIds: [accountId],
          }),
        }
      );

      // Move back to ungrouped
      setAccounts((prev) =>
        prev.map((a) =>
          a.accountId === accountId
            ? { ...a, companyGroup: null, groupName: null }
            : a
        )
      );

    } catch (err) {
      console.error("Error removing from group", err);
      alert("Failed to remove");
    }
  };

  // DELETE GROUP
  const deleteGroup = async (groupId) => {
    const affected = accounts.filter((a) => a.companyGroup === groupId);
    const accountIds = affected.map((a) => a.accountId);

    if (!window.confirm("Delete this group and ungroup its accounts?"))
      return;

    try {
      await fetchAuthenticated(
        `${API_BASE_URL}/companyProfile/deleteGrouping`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ groupingId: groupId, accountIds }),
        }
      );

      // Ungroup accounts
      setAccounts((prev) =>
        prev.map((acc) =>
          acc.companyGroup === groupId
            ? { ...acc, companyGroup: null, groupName: null }
            : acc
        )
      );

      // Remove group from list
      setGroups((prev) => prev.filter((g) => g.id !== groupId));

    } catch (err) {
      console.error("Error deleting group", err);
      alert("Failed to delete");
    }
  };

  // RENDER
  const populatedGroups = groups.filter((g) =>
    accounts.some((acc) => acc.companyGroup === g.id)
  );

  return (
    <div className="group-manager">
      <div className="panel-header">
        <div className="panel-header-text">
          <h2>
            Company groups
            <span className="count-pill">{populatedGroups.length}</span>
          </h2>
          <p>Group companies to consolidate them together in a report.</p>
        </div>

        <div className="create-group-row">
          <input
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createGroup()}
            placeholder="New group name"
            aria-label="New group name"
          />
          <button className="btn btn-brand btn-sm" onClick={createGroup}>
            Create
          </button>
        </div>
      </div>

      <div className="panel-body">
        {/* ASSIGN BOX */}
        <div className="assign-box">
          <div className="assign-box-head">
            <h3>Assign companies to a group</h3>
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              aria-label="Destination group"
            >
              <option value="">Move selected to…</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.groupingName}
                </option>
              ))}
            </select>
          </div>

          <ul className="account-list">
            {accounts.map((acc) => (
              <li className="group-row" key={acc.accountId}>
                <label>
                  <input
                    type="checkbox"
                    checked={selectedToMove.has(acc.accountId)}
                    onChange={() => toggleSelect(acc.accountId)}
                  />
                  {acc.companyName}
                </label>
              </li>
            ))}
          </ul>

          <div className="assign-controls-bottom">
            <span className="muted">
              {selectedToMove.size
                ? `${selectedToMove.size} selected`
                : "No companies selected"}
            </span>
            <button
              className="btn btn-brand btn-sm"
              onClick={assignToGroup}
              disabled={!selectedToMove.size || !selectedGroupId}
            >
              Assign selected
            </button>
          </div>
        </div>

        {/* GROUP CARDS */}
        <div className="group-cards">
          {populatedGroups.map((g) => {
            const groupAccounts = accounts.filter(
              (acc) => acc.companyGroup === g.id
            );

            return (
              <div className="group-card" key={g.id}>
                <div className="group-card-header">
                  <h3>
                    {g.groupingName}
                    <span className="count-pill">{groupAccounts.length}</span>
                  </h3>

                  <button
                    className="btn btn-neutral btn-sm btn-danger-text"
                    onClick={() => deleteGroup(g.id)}
                  >
                    Delete
                  </button>
                </div>

                <ul className="account-list">
                  {groupAccounts.map((acc) => (
                    <li className="group-row" key={acc.accountId}>
                      {acc.companyName}

                      <button
                        className="icon-btn"
                        onClick={() => removeFromGroup(acc.accountId)}
                        title={`Remove ${acc.companyName} from ${g.groupingName}`}
                        aria-label={`Remove ${acc.companyName} from ${g.groupingName}`}
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* UNGROUPED */}
          {ungrouped.length > 0 && (
            <div className="group-card group-card--muted">
              <div className="group-card-header">
                <h3>
                  Ungrouped
                  <span className="count-pill">{ungrouped.length}</span>
                </h3>
              </div>

              <ul className="account-list">
                {ungrouped.map((acc) => (
                  <li className="group-row" key={acc.accountId}>
                    {acc.companyName}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompanyGrouping;
